import {
  getBackendID,
  getCustomerToken,
  getPassiveSessionId,
  getRequestorPublicKey,
} from "../data/customerTokens";
import axios from "../lib/axios";
import { isAxiosTransportError } from "../lib/axiosError";
import { getBrowserForwardedHeaders } from "../lib/forwardedHeaders";
import { getRequestor, getSettings } from "../lib/functions";
import Logger from "../services/Logger";

const sharedConfig = require("../config/sharedConfig");

type GolApiCallOptions = {
  customerToken?: string;
  requestorPublicKey?: string;
  clientId?: string;
  language?: string;
  alternativeCurrency?: string;
  golApiUrl?: string;
  forwardedHeaders?: Record<string, string>;
};

export const getApiHeader = async (
  options?: {
    forcedCustomerToken?: string;
    forcedRequestorPublicKey?: string;
    forcedClientId?: string;
    language?: string;
    defaultCountry?: string;
    alternativeCurrency?: string;
  },
  userConfirmationalToken = null
) => {
  const forcedCustomerToken = options?.forcedCustomerToken;
  const forcedRequestorPublicKey = options?.forcedRequestorPublicKey;
  const forcedClientId = options?.forcedClientId;

  const requestorPublicKey =
    forcedRequestorPublicKey && forcedRequestorPublicKey.length > 0
      ? forcedRequestorPublicKey
      : await getRequestorPublicKey();

  const Authorization: any = requestorPublicKey
    ? {
        RequestorPublicKey: { $t: requestorPublicKey },
        UserToken: { $t: forcedCustomerToken },
      }
    : { Requestor: getRequestor(forcedClientId) };

  const customerToken =
    forcedCustomerToken && forcedCustomerToken.length > 0
      ? forcedCustomerToken
      : await getCustomerToken();

  if (customerToken) {
    Authorization.UserToken = { $t: customerToken };
  }

  if (userConfirmationalToken) {
    Authorization.UserToken = { $t: userConfirmationalToken };
  }

  const apiHeaderSettingsOptions = options
    ? {
        ...(options?.language ? { language: options.language } : {}),
        ...(options?.defaultCountry
          ? { defaultCountry: options.defaultCountry }
          : {}),
        ...(options?.alternativeCurrency
          ? { alternativeCurrency: options.alternativeCurrency }
          : {}),
      }
    : undefined;

  const apiHeader = {
    PassiveSessionId: await getPassiveSessionId(),
    Authorization: { ...Authorization },
    Settings: await getSettings(apiHeaderSettingsOptions),
  };

  return apiHeader;
};

export default async (
  golRequest,
  options: GolApiCallOptions | null = null,
  userConfirmationalToken = null
) => {
  try {
    const golApiHeader = await getApiHeader(
      options
        ? {
            forcedCustomerToken: options.customerToken,
            forcedRequestorPublicKey: options.requestorPublicKey,
            forcedClientId: options.clientId,
            ...(options?.language ? { language: options.language } : {}),
            ...(options?.alternativeCurrency
              ? { alternativeCurrency: options.alternativeCurrency }
              : {}),
          }
        : undefined,
      userConfirmationalToken
    );

    const golRequestToSend = {
      GolApi: {
        ...golApiHeader,
        ...golRequest.GolApi,
        ...(golApiHeader?.Settings?.AlternativeCurrency
          ? {
              Settings: {
                ...golApiHeader.Settings,
                ...golRequest.GolApi.Settings,
                AlternativeCurrency: golApiHeader.Settings.AlternativeCurrency,
              },
            }
          : {}),
      },
    };

    const backendId = getBackendID();

    const golApiUrlEnv =
      process?.env?.NEXT_PUBLIC_D4_golApiUrl ||
      (typeof window !== "undefined" &&
        window?.__ENV?.NEXT_PUBLIC_D4_golApiUrl);

    const golApiUrl = golApiUrlEnv || sharedConfig.golApiUrl;

    // Merge headers: server-side forwardedHeaders + client-side browser headers
    // Browser headers (Gol-Dealer-Host) are added via axios interceptor,
    // but we need to ensure they're included even when forwardedHeaders are provided
    const browserHeaders =
      typeof window !== "undefined" ? getBrowserForwardedHeaders() : {};
    const mergedHeaders = options?.forwardedHeaders
      ? { ...browserHeaders, ...options.forwardedHeaders }
      : browserHeaders;

    const response = await axios.post(
      (options?.golApiUrl || golApiUrl) +
        (backendId ? `?backend_id=${backendId}` : ""),
      golRequestToSend,
      Object.keys(mergedHeaders).length > 0
        ? { headers: mergedHeaders }
        : undefined
    );

    return response.data.GolApi;
  } catch (e) {
    if (isAxiosTransportError(e)) {
      Logger.error(e, { reportedTransportError: true });
    } else {
      Logger.error(e);
    }
    return { error: e };
  }
};
